import {
  REQUEST_REFRESH_RESOURCE_APPLY,
  REQUEST_REFRESH_RESOURCE_CLEAR,
  REQUEST_REFRESH_RESOURCE_NO_OP,
  readBilledToFacilityFromRequest,
  readBilledToFacilityFromState,
  readBillerVerificationFromRequest,
  readBillerVerificationFromState,
  readChiefComplaintFromRequest,
  readChiefComplaintFromState,
  readCoveragesFromRequest,
  readCoveragesFromState,
  readPatientFromRequest,
  readPatientFromState,
} from './requestRefresh.helper';
import { formatDateWithoutTimezone } from '../../helpers/dateFormatter';

export const REQUEST_REFRESH_DIALOG_ACTION_REFRESH = 'refresh';
export const REQUEST_REFRESH_DIALOG_ACTION_NO_OP = 'no-op';

export const REQUEST_CHANGED_MESSAGE = 'Request information has changed. Please refresh.';
export const REQUEST_CHANGED_WITH_UNSAVED_MESSAGE =
  'Request information has changed. Please refresh. Your current changes will not be lost.';
export const TRIP_UPDATED_MESSAGE =
  'Request information has updated. Please save your changes and re-run the Eligibility Agent.';

const normalizeValue = value => {
  if (value === undefined || value === null) return '';
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  return String(value).trim();
};

const normalizeDate = value => {
  if (!value) return '';

  // Compare DOB by calendar date only. formatDateWithoutTimezone strips the time
  // component before parsing, so equal calendar dates never diff due to timezone.
  return formatDateWithoutTimezone(normalizeValue(value)) || '';
};

const normalizePatient = fields => ({
  firstName: normalizeValue(fields.firstName),
  lastName: normalizeValue(fields.lastName),
  dob: normalizeDate(fields.dob),
  address1: normalizeValue(fields.address1),
  address2: normalizeValue(fields.address2),
  city: normalizeValue(fields.city),
  state: normalizeValue(fields.state),
  zip: normalizeValue(fields.zip),
});

const normalizeCoverage = coverage => ({
  payerId: normalizeValue(coverage?.payer?.payerId || coverage?.payerId),
  payerName: normalizeValue(coverage?.payer?.payerName || coverage?.payerName),
  policyNumber: normalizeValue(coverage?.policyNumber),
  authorizationNumber: normalizeValue(coverage?.authorizationNumber),
  isActive: normalizeValue(!!coverage?.isActive),
});

const normalizeCoverages = coverages => {
  const normalized = {};

  Object.keys(coverages || {}).forEach(coordinationTypeId => {
    normalized[coordinationTypeId] = normalizeCoverage(coverages[coordinationTypeId]);
  });

  return normalized;
};

const normalizeChiefComplaint = chiefComplaint => ({
  chiefComplaintId: normalizeValue(chiefComplaint.chiefComplaintId),
  chiefComplaintName: normalizeValue(chiefComplaint.chiefComplaintName),
  chiefComplaintType: normalizeValue(chiefComplaint.chiefComplaintType),
});

const normalizeBillerVerification = fields => {
  const isBillerVerified = !!fields.isBillerVerified;

  return {
    isBillerVerified: normalizeValue(isBillerVerified),
    billerVerifiedAt: isBillerVerified ? normalizeValue(fields.billerVerifiedAt) : '',
    billerVerifiedBy: isBillerVerified ? normalizeValue(fields.billerVerifiedBy) : '',
  };
};

const isObject = value => value && typeof value === 'object';

const changed = (current, latest) => {
  const keys = new Set([...Object.keys(current || {}), ...Object.keys(latest || {})]);

  return [...keys].some(key => {
    if (isObject(current?.[key]) || isObject(latest?.[key])) {
      return changed(current?.[key] || {}, latest?.[key] || {});
    }

    return normalizeValue(current?.[key]) !== normalizeValue(latest?.[key]);
  });
};

export const getRequestRefreshFields = request => ({
  patient: normalizePatient(readPatientFromRequest(request)),
  coverages: normalizeCoverages(readCoveragesFromRequest(request)),
  chiefComplaint: normalizeChiefComplaint(readChiefComplaintFromRequest(request)),
  isBilledToFacility: normalizeValue(readBilledToFacilityFromRequest(request)),
  billerVerification: normalizeBillerVerification(readBillerVerificationFromRequest(request)),
});

export const getCurrentRequestRefreshFields = state => ({
  patient: normalizePatient(readPatientFromState(state)),
  coverages: normalizeCoverages(readCoveragesFromState(state)),
  chiefComplaint: normalizeChiefComplaint(readChiefComplaintFromState(state)),
  isBilledToFacility: normalizeValue(readBilledToFacilityFromState(state)),
  billerVerification: normalizeBillerVerification(readBillerVerificationFromState(state)),
});

export const getDirtyState = state => ({
  patient: !!state.patient.dirty,
  coverages: state.infobox.dirtyCoverages || {},
  isBilledToFacility: !!state.infobox.billedToFacilityChange,
  chiefComplaint: !!state.requests.chiefComplaintDirty,
});

const hasDirtyRefreshResources = dirtyState =>
  dirtyState.patient ||
  Object.keys(dirtyState.coverages).length > 0 ||
  dirtyState.isBilledToFacility ||
  dirtyState.chiefComplaint;

const getChangedCoverages = (currentCoverages, latestCoverages) => {
  const changes = {};
  const coordinationTypeIds = new Set([...Object.keys(currentCoverages || {}), ...Object.keys(latestCoverages || {})]);

  coordinationTypeIds.forEach(coordinationTypeId => {
    if (changed(currentCoverages?.[coordinationTypeId] || {}, latestCoverages?.[coordinationTypeId] || {})) {
      changes[coordinationTypeId] = true;
    }
  });

  return changes;
};

export const getChangedRequestResources = (state, latestRequest) => {
  const currentFields = getCurrentRequestRefreshFields(state);
  const latestFields = getRequestRefreshFields(latestRequest);

  return {
    patient: changed(currentFields.patient, latestFields.patient),
    coverages: getChangedCoverages(currentFields.coverages, latestFields.coverages),
    chiefComplaint: changed(currentFields.chiefComplaint, latestFields.chiefComplaint),
    isBilledToFacility: currentFields.isBilledToFacility !== latestFields.isBilledToFacility,
    billerVerification: changed(currentFields.billerVerification, latestFields.billerVerification),
  };
};

const getConflictResources = (dirtyState, changedResources) => ({
  patient: dirtyState.patient && changedResources.patient,
  coverages: Object.keys(changedResources.coverages).reduce((acc, coordinationTypeId) => {
    if (dirtyState.coverages[coordinationTypeId]) {
      acc[coordinationTypeId] = true;
    }

    return acc;
  }, {}),
  chiefComplaint: dirtyState.chiefComplaint && changedResources.chiefComplaint,
  isBilledToFacility: dirtyState.isBilledToFacility && changedResources.isBilledToFacility,
});

const hasCoverageChanges = changes => Object.keys(changes).length > 0;

const hasConflicts = conflicts =>
  conflicts.patient ||
  hasCoverageChanges(conflicts.coverages) ||
  conflicts.chiefComplaint ||
  conflicts.isBilledToFacility;

const REQUEST_REFRESH_RESOURCE_LABELS = {
  patient: 'Patient',
  coverages: 'Coverage',
  chiefComplaint: 'Chief Complaint',
  isBilledToFacility: 'Billed to Facility',
};

const getCoverageLabel = (coordinationTypeId, coordinationTypes) => {
  const coordinationType = (coordinationTypes || []).find(type => String(type.id) === String(coordinationTypeId));

  return coordinationType?.displayName || coordinationType?.name || REQUEST_REFRESH_RESOURCE_LABELS.coverages;
};

const getConflictResourceLabels = (conflictResources, coordinationTypes) => {
  const labels = [];

  if (conflictResources.patient) labels.push(REQUEST_REFRESH_RESOURCE_LABELS.patient);

  Object.keys(conflictResources.coverages).forEach(coordinationTypeId => {
    labels.push(getCoverageLabel(coordinationTypeId, coordinationTypes));
  });

  if (conflictResources.chiefComplaint) labels.push(REQUEST_REFRESH_RESOURCE_LABELS.chiefComplaint);
  if (conflictResources.isBilledToFacility) labels.push(REQUEST_REFRESH_RESOURCE_LABELS.isBilledToFacility);

  return labels;
};

const getResourceRefreshAction = shouldApply =>
  shouldApply ? REQUEST_REFRESH_RESOURCE_APPLY : REQUEST_REFRESH_RESOURCE_NO_OP;

const getRequestRefreshResourcesToApply = (changedResources, conflictResources) => {
  const conflicts = hasConflicts(conflictResources);

  return {
    patient: getResourceRefreshAction(changedResources.patient && !conflictResources.patient),
    coverages: Object.keys(changedResources.coverages).reduce((acc, coordinationTypeId) => {
      acc[coordinationTypeId] = conflictResources.coverages[coordinationTypeId]
        ? REQUEST_REFRESH_RESOURCE_NO_OP
        : REQUEST_REFRESH_RESOURCE_APPLY;

      return acc;
    }, {}),
    chiefComplaint: getResourceRefreshAction(changedResources.chiefComplaint && !conflictResources.chiefComplaint),
    isBilledToFacility: getResourceRefreshAction(
      changedResources.isBilledToFacility && !conflictResources.isBilledToFacility,
    ),
    billerVerification: conflicts
      ? REQUEST_REFRESH_RESOURCE_CLEAR
      : getResourceRefreshAction(changedResources.billerVerification),
  };
};

const REFRESH_DIALOG = {
  buttonText: 'REFRESH',
  buttonAction: REQUEST_REFRESH_DIALOG_ACTION_REFRESH,
  hideCancel: false,
  conflicts: [],
};

const ACKNOWLEDGE_DIALOG = {
  buttonText: 'OK',
  buttonAction: REQUEST_REFRESH_DIALOG_ACTION_NO_OP,
  hideCancel: true,
  conflicts: [],
};

const resolveRequestRefreshState = (state, latestRequest) => {
  const dirtyState = getDirtyState(state);
  const emptyConflictResources = {
    patient: false,
    coverages: {},
    chiefComplaint: false,
    isBilledToFacility: false,
  };

  const emptyResourcesToApply = {
    patient: REQUEST_REFRESH_RESOURCE_NO_OP,
    coverages: {},
    chiefComplaint: REQUEST_REFRESH_RESOURCE_NO_OP,
    isBilledToFacility: REQUEST_REFRESH_RESOURCE_NO_OP,
    billerVerification: REQUEST_REFRESH_RESOURCE_NO_OP,
  };

  if (!hasDirtyRefreshResources(dirtyState)) {
    const changedResources = latestRequest
      ? getChangedRequestResources(state, latestRequest)
      : {
          patient: true,
          coverages: {},
          chiefComplaint: true,
          isBilledToFacility: true,
          billerVerification: true,
        };

    return {
      ...REFRESH_DIALOG,
      message: REQUEST_CHANGED_MESSAGE,
      resourcesToApply: latestRequest
        ? getRequestRefreshResourcesToApply(changedResources, emptyConflictResources)
        : emptyResourcesToApply,
    };
  }

  if (!latestRequest) {
    return {
      ...ACKNOWLEDGE_DIALOG,
      message: TRIP_UPDATED_MESSAGE,
      resourcesToApply: emptyResourcesToApply,
    };
  }

  const changedResources = getChangedRequestResources(state, latestRequest);
  const conflictResources = getConflictResources(dirtyState, changedResources);
  const resourcesToApply = getRequestRefreshResourcesToApply(changedResources, conflictResources);

  if (hasConflicts(conflictResources)) {
    return {
      ...ACKNOWLEDGE_DIALOG,
      message: TRIP_UPDATED_MESSAGE,
      conflicts: getConflictResourceLabels(conflictResources, state.infobox.coordinationTypes),
      resourcesToApply,
    };
  }

  return {
    ...REFRESH_DIALOG,
    message: REQUEST_CHANGED_WITH_UNSAVED_MESSAGE,
    resourcesToApply,
  };
};

export const resolveRequestRefreshResourcesToApply = (state, latestRequest) =>
  resolveRequestRefreshState(state, latestRequest).resourcesToApply;

export const resolveRequestRefreshDialog = (state, latestRequest) => {
  const { message, buttonText, buttonAction, hideCancel, conflicts } = resolveRequestRefreshState(state, latestRequest);

  return {
    message,
    buttonText,
    buttonAction,
    hideCancel,
    conflicts,
  };
};
